import { DialogPlugin } from 'tdesign-vue';
import { promises } from 'original-fs';
import i18n, { i18n_t } from '@/locales/i18n';
import { IpcEvents } from '@/ipc-events';
import ipc from '@/renderer/scripts/utils/ipc';
import storage from '@/renderer/scripts/utils/storage/index';
import getRequest from '@/renderer/scripts/httpRequest';
import { DownloadSetting } from '@/renderer/scripts/utils/storage/StoreKey';
import { IS_BROWSER } from '@/common/constant/platform';

import tips from '../tips';
interface DownloadToLocalParams {
  url: string;
  downloadPath: string;
  downloadName: string;
  autoRename?: boolean;
  openDirAfterDownload?: boolean;
}
interface DownloadFileContent {
  downloadTarget: string;
  fileName: string;
  fileContent: string | Buffer;
}
let fs: typeof import('fs');
let path: typeof import('path');
if (!IS_BROWSER) {
  fs = (window.parent as any).require('fs') as typeof import('fs');
  path = (window.parent as any).require('path') as typeof import('path');
}

async function selectDownloadFolder(path: string) {
  const { filePaths } = await ipc.invoke(IpcEvents.INVOKE_OPERATE_MAIN_LISTENER, {
    event: 'selectFolders',
    params: {
      defaultPath: path,
    },
  });
  if (!filePaths || !filePaths.length) return '';
  return filePaths[0];
}

function checkPathPermission(path: string) {
  let downloadTarget = path;
  if (!downloadTarget) {
    return '';
  }
  let canWirte = false;
  // 判断有没有该目录的写权限
  try {
    fs.accessSync(path, fs.constants.W_OK);
    canWirte = true;
  } catch (e) {
    canWirte = false;
    downloadTarget = '';
  }
  if (!canWirte) {
    tips.error(i18n_t('没有操作该目录的权限，请重新选择'));
  }
  return downloadTarget;
}

function comfirmDownloadPath(): Promise<string> {
  return new Promise((resolve, reject) => {
    const comfirmDlg = DialogPlugin.confirm({
      header: i18n_t('提示'),
      body: i18n_t('默认下载路径失效，是否重新选择默认路径并下载'),
      theme: 'warning',
      closeBtn: false,
      closeOnEscKeydown: false,
      closeOnOverlayClick: false,
      onConfirm: async () => {
        comfirmDlg.hide();
        resolve(await selectDownloadFolder(''));
      },
      onCancel: async () => {
        comfirmDlg.hide();
        resolve('');
      },
    });
  });
}

// pc端获取下载目标路径
export async function getDownloadPath(): Promise<string> {
  let downloadTarget = '';
  try {
    const storageDownloadSetting: DownloadSetting = storage.get<DownloadSetting>(storage.StoreKey.DOWNLOAD_SETTING);
    if (storageDownloadSetting.noAskDownloadPath && storageDownloadSetting.downloadPath) {
      if (!fs.existsSync(storageDownloadSetting.downloadPath)) {
        // 旧的路径失效了，重新选择默认路径
        downloadTarget = await comfirmDownloadPath();
        if (downloadTarget) {
          storage.set(storage.StoreKey.DOWNLOAD_SETTING, {
            downloadPath: downloadTarget,
            noAskDownloadPath: storageDownloadSetting.noAskDownloadPath,
          });
        }
      } else {
        downloadTarget = storageDownloadSetting.downloadPath;
      }
    } else {
      downloadTarget = await selectDownloadFolder(storageDownloadSetting.downloadPath);
    }
  } catch (e) {
    logger.ui.error('获取下载目录失败', JSON.stringify(e));
    return '';
  }
  return await checkPathPermission(downloadTarget);
}

export function downloadUrlFileToLocal(params: DownloadToLocalParams): void {
  if (IS_BROWSER) return;
  const { url, downloadPath, downloadName, autoRename, openDirAfterDownload } = params;
  const request = getRequest({
    logPrefix: 'DownloadUrlFileToLocal',
  });
  // const request = (window.parent as any).require('request');
  let filePath = path.join(downloadPath, downloadName);
  // 文件已存在需要重命名
  if (autoRename) {
    let i = 1;
    const lastIndex = downloadName.lastIndexOf('.');
    const ext = downloadName.slice(lastIndex);
    const name = downloadName.slice(0, lastIndex);
    while (i <= 1000000 && fs.existsSync(filePath)) {
      filePath = path.resolve(downloadPath, `${name} (${i})${ext}`);
      i++;
    }
  }
  const stream = fs.createWriteStream(filePath);
  request({
    url,
    method: 'get',
    responseType: 'stream',
  })
    .then(res => {
      res.data.pipe(stream).on('close', (err: any) => {
        tips.success(i18n_t('下载成功'));
        if (openDirAfterDownload) {
          ipc.send(IpcEvents.ON_OPERATE_MAIN_LISTENER, {
            event: 'openPath',
            params: {
              path: filePath,
            },
          });
        }
      });
    })
    .catch(err => {
      tips.success(i18n_t('下载失败'));
    });
}

/**
 * pc端拿到文件内容后下载到本地
 * downloadTarget：下载路径，从本地设置读取或用户选择
 * fileName：完整文件名比如123.png
 * fileContent：文件内容
 * */
export const downloadFileContent = ({ downloadTarget, fileName, fileContent }: DownloadFileContent) => {
  if (!downloadTarget || !fileName || !fileContent) {
    console.warn('missing downloadFile params');
    return;
  }
  let filePrefix = fileName;
  let fileExt = '';
  if (fileName.includes('.')) {
    const lastIndex = fileName.lastIndexOf('.');
    fileExt = fileName.slice(lastIndex);
    filePrefix = fileName.slice(0, lastIndex);
  }
  let savePath = path.resolve(downloadTarget, `${filePrefix}${fileExt}`);
  // 文件已存在需要重命名
  let i = 1;
  while (i <= 1000000 && fs.existsSync(savePath)) {
    savePath = path.resolve(downloadTarget, `${filePrefix}(${i})${fileExt}`);
    i++;
  }
  try {
    fs.writeFileSync(savePath, fileContent);
    ipc.send(IpcEvents.ON_OPERATE_MAIN_LISTENER, {
      event: 'openPath',
      params: {
        path: savePath,
      },
    });
    return true;
  } catch (e) {
    tips.error(i18n_t('保存失败'));
    return false;
  }
};
